import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { ErrorBoundary } from '@lusha/client-utils';
import { ExperimentsProvider, useAttributes } from '@lusha/experiment-react-sdk';
import { BrazeProvider } from '@lusha/lusha-braze';
import { AnalyticsContextProvider } from '@lusha-engage/analytics';
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import LushaIntercomProvider from './LushaIntercomProvider';
import config from './config';
import { useAppDispatch, useAppSelector } from './reducers';
import SettingsDispatcher from './reducers/settings/dispatcher';
import { APP_ID } from './utils/constants';
import { diffBetweenDates } from './utils/date';
import ErrorScreen from './views/ErrorScreen';
function ExperimentAttributesSync() {
    const { registerDate } = useAppSelector(state => ({ registerDate: state.user.analytics.registerDate }));
    const { mergeAttributes } = useAttributes();
    useEffect(() => {
        var _a, _b;
        if (!registerDate)
            return;
        mergeAttributes({
            daysSinceRegistration: Math.floor((_b = (_a = diffBetweenDates(new Date(), registerDate)) === null || _a === void 0 ? void 0 : _a.days) !== null && _b !== void 0 ? _b : 0),
        });
    }, [registerDate]);
    return null;
}
function AppUserContext(props) {
    const { children } = props;
    const dispatch = useAppDispatch();
    const { user, version, platform } = useAppSelector(state => ({
        user: state.user,
        version: state.settings.version,
        platform: state.settings.platform,
    }));
    const location = useLocation();
    const isExtensionMode = useMemo(() => {
        /*
         * Indicate that extension is opened in minimal mode (by extension click). (not side bar)
         * maybe there is better way to detect it, but I didn't find
         * */
        return location.pathname === '/toolbar-popup';
    }, []);
    // Optimizely is being removed. The experiment SDK is initialized with `loggedUsersOnly: true`,
    // so it only ever fetches flags when `isLoggedIn` is true. Forcing this to `false` guarantees the
    // FE never sends flag requests (preload settings, useFlag queries and flag.load() are all gated by
    // this), and every consumer falls back to the control group. Experiment code is kept in place so
    // the AB tests can be restored later by uncommenting the block below.
    // const isFlagsEnabled =
    //   !isExtensionMode &&
    //   !!settings.isPluginOpen && // tmp disabled to test new exclusive flag
    //   !!user?.id
    const isFlagsEnabled = !isExtensionMode && !!(user === null || user === void 0 ? void 0 : user.id);
    useEffect(() => {
        dispatch(SettingsDispatcher.actions.set({
            isExtensionMode,
        }));
    }, [isExtensionMode]);
    const event_additional_info = useMemo(() => ({
        plugin_version: config.version,
        extension_app_version: version,
    }), [version]);
    return (_jsx(ErrorBoundary, { additionalInfo: { subPlatform: platform }, customErrorComponent: _jsx(ErrorScreen, {}), children: _jsxs(ExperimentsProvider, { appId: APP_ID, isLoggedIn: isFlagsEnabled, children: [_jsx(ExperimentAttributesSync, {}), _jsx(BrazeProvider, { user: user, children: _jsx(LushaIntercomProvider, { isExtensionMode: isExtensionMode, children: _jsx(AnalyticsContextProvider, { teamName: "plugin", product: "plugin", event_page_state_str: 'plugin', subPlatform: platform, event_additional_info: event_additional_info, children: children }) }) })] }) }));
}
export default AppUserContext;
